import { useCallback } from 'react';
import LogoImg from '../images/isa2.png';
import LogoImgSad from '../images/isa_sad2.png';
import { emprestimoTodasOpcoes, defaultOptions, atendimentoOptions } from '../chatbotOptions';
import Api from '../../../service/api';

/**
 * All message-sending logic: user messages, bot responses, file uploads,
 * typing status and option-as-message flows.
 */
const useMessageHandlers = ({
    apiCHAT,
    tokenChat,
    profile,
    selectedSessionId,
    selectedSession,
    newMessage,
    setNewMessage,
    newTipoChat,
    setMessages,
    setOptions,
    setShowOptions,
    setShowInputBar,
    mqttClientRef,
    isMountedRef,
    markMessagesAsRead,
    comunicacaoInternaData,
    setComunicacaoInternaData,
    handleSelectOptionRef,
    setIsBotTyping,
    setLoadingPage,
    calcularSeguro,
}) => {
    // ─── URL helper ───────────────────────────────────────────────────────────

    const urlChat = useCallback((path) => `${apiCHAT.defaults.baseURL}${path}`, [apiCHAT]);

    // ─── Intention detection ──────────────────────────────────────────────────

    const handleDetectarIntencao = useCallback(
        async (mensagem, type) => {
            if (!tokenChat) return { intencao: null };
            try {
                const response = await apiCHAT.get('/chat/detector_intencoes', {
                    params: { pergunta: mensagem, type },
                    headers: { Authorization: `Bearer ${tokenChat}` },
                });
                return response.data;
            } catch {
                return { intencao: null };
            }
        },
        [apiCHAT, tokenChat],
    );

    //

    const handleDetectarIntencaoMenu = useCallback(
        async (mensagem) => {
            if (!tokenChat) return { intencao: null };
            try {
                const response = await apiCHAT.get('/chat/detector_intencoes_menu', {
                    params: { pergunta: mensagem },
                    headers: { Authorization: `Bearer ${tokenChat}` },
                });
                return response.data;
            } catch {
                return null;
            }
        },
        [apiCHAT, tokenChat],
    );

    // ─── MQTT broadcast helpers ───────────────────────────────────────────────

    const broadcastSessionUpdate = useCallback(
        (sessionId, status, additionalData = {}) => {
            if (!sessionId || !status) return;

            if (mqttClientRef.current?.connected) {
                const updateData = {
                    sessionId,
                    status,
                    userId: profile.pess_id_pessoa,
                    timestamp: new Date().toISOString(),
                    userName: profile.pess_nome || 'Usuario',
                    type: 'session_update',
                    ...additionalData,
                };

                try {
                    mqttClientRef.current.publish('chatbot-web/', JSON.stringify(updateData));
                    mqttClientRef.current.publish(
                        `chatbot-web/session-${sessionId}`,
                        JSON.stringify({ ...updateData, type: 'status_change' }),
                    );
                } catch (error) {
                    console.error('Error broadcasting session update:', error);
                }
            }
        },
        [mqttClientRef, profile.pess_id_pessoa, profile.pess_nome],
    );

    const broadcastBotMessage = useCallback(
        (botMessage) => {
            if (mqttClientRef.current?.connected) {
                mqttClientRef.current.publish(
                    'chatbot-web/',
                    JSON.stringify({
                        sessionId: selectedSessionId,
                        userId: 'ISA',
                        userName: 'ISA',
                        messageText: botMessage,
                        timestamp: new Date().toISOString(),
                        type: 'new_message_received',
                    }),
                );
            }
        },
        [mqttClientRef, selectedSessionId],
    );

    // ─── Private message-processing helpers ───────────────────────────────────

    const validateAsOptions = (optionKey, pergunta) => `${optionKey}: ${pergunta}`;

    const prepareMessageData = useCallback(
        (messageText, nomeUsuario, letraInicial) => ({
            remetente: nomeUsuario,
            texto: messageText,
            avatar: letraInicial,
        }),
        [],
    );

    const processConsignadoOptions = useCallback((tipoChat, messageText) => {
        const consignadoOptions = [
            'credito_consignado',
            'credito_calcular_simulacao',
            'credito_valor_total_calcular_simulacao',
            'credito_salvar_simulacao',
            'credito_salvar_dados_complementares',
            'emprestimo_abrir_janela_atualizacao',
            'credito_cancelar',
            'credito_consignado_aprovada_aderir_proposta',
        ];

        if (consignadoOptions.includes(tipoChat)) {
            return { messageText: validateAsOptions(tipoChat, messageText), tipoChat: 'credito_consignado' };
        }
        return { messageText, tipoChat };
    }, []);

    const processComunicacaoInternaOptions = useCallback((tipoChat, messageText) => {
        const comunicacaoInternaOptions = [
            'data_inicio_comunicacao_interna',
            'data_fim_comunicacao_interna',
            'cid_comunicacao_interna',
            'comentario_comunicacao_interna',
            'arquivo_comunicacao_interna',
        ];

        if (comunicacaoInternaOptions.includes(tipoChat)) {
            return { messageText: validateAsOptions(tipoChat, messageText), tipoChat: 'comunicacao_interna' };
        }
        return { messageText, tipoChat };
    }, []);

    const shouldDisplayMessageImmediately = useCallback((tipoChat) => {
        const skipDisplayTypes = [
            'AGUARDANDO_HUMANDO',
            'PENDENTE_HUMANO',
            'ATENDIMENTO_AUSENCIA_PENDENTE',
            'ATENDIMENTO_AUSENCIA_EM_ANDAMENTO',
            'informacoes',
            'gerar_holerite',
            'informe_de_rendimento',
            'COMUNICACAO_RESPONSAVEL_PENDENTE',
            'COMUNICACAO_AT_HUMANDO',
            'conversar_comunicacao_interna',
            'conversar_emprestimo',
            'EMPRESTIMO_EM_ATEND',
            'COMUNICACAO_ANDAMENTO_DASHBOARD',
        ];
        return !skipDisplayTypes.includes(tipoChat);
    }, []);

    const processReportGeneration = useCallback((tipoChat, messageText) => {
        const reportTypes = ['gerar_holerite', 'informe_de_rendimento'];
        if (reportTypes.includes(tipoChat)) {
            return { tipoChatPergunta: tipoChat, tipoChat: 'informacoes', messageText: `${messageText} ${tipoChat}` };
        }
        return { tipoChatPergunta: '', tipoChat, messageText };
    }, []);

    // ─── Bot response handlers ────────────────────────────────────────────────

    const handleConsignadoResponse = useCallback(
        async (response, newTipoChatArg, respostaBot) => {
            setShowOptions(false);
            setShowInputBar(false);

            const consignadoTypes = [
                'credito_consignado',
                'credito_calcular_simulacao',
                'credito_valor_total_calcular_simulacao',
                'credito_salvar_simulacao',
                'credito_salvar_dados_complementares',
                'emprestimo_abrir_janela_atualizacao',
                'credito_cancelar',
                'credito_finalizar',
                'credito_consignado_aprovada_aderir_proposta',
                'credito_consignado_averbada',
            ];

            if (!consignadoTypes.includes(newTipoChatArg)) return false;

            const label = emprestimoTodasOpcoes.find((item) => item.key === newTipoChatArg);
            const intencaoData = await handleDetectarIntencao(response.data.resposta, newTipoChatArg);

            setMessages((hist) => [...hist, respostaBot]);
            broadcastBotMessage(response.data.resposta);

            const existingProposalIntentions = [
                'credito_consignado_averbada',
                'credito_consignado_efetivada',
                'credito_consignado_paga',
                'credito_consignado_negada',
                'credito_consignado_cancelada',
                'credito_consignado_ag_aprovacao',
            ];

            if (existingProposalIntentions.includes(intencaoData?.intencao)) {
                setOptions([
                    { key: 'voltar_questoes', label: 'Voltar para as questões anteriores' },
                    { key: 'credito_cancelar_pre', label: 'Cancelar proposta existente' },
                ]);
                setShowOptions(true);
            } else if (intencaoData?.intencao === 'credito_consignado_aprovada_aderir_proposta') {
                handleSelectOptionRef.current?.(
                    'credito_consignado_aprovada_aderir_proposta',
                    'Avaliar antes de aderir',
                );
            } else if (intencaoData?.intencao === 'credito_consignado_averbada') {
                handleSelectOptionRef.current?.('credito_consignado_averbada', 'Ver status do meu empréstimo');
            } else if (intencaoData?.intencao === 'emprestimo_inicio') {
                handleSelectOptionRef.current?.('emprestimo_inicio', 'Tentar novamente');
            } else if (intencaoData?.intencao === 'pendente_assinatura') {
                handleSelectOptionRef.current?.(
                    'emprestimo_tentar_novamente',
                    'Tentar novamente depois de assinar a autorização de consulta de dados',
                );
            } else {
                if (label) handleSelectOptionRef.current?.(label.key, label.label);
            }

            return true;
        },
        [
            handleDetectarIntencao,
            broadcastBotMessage,
            setMessages,
            setOptions,
            setShowOptions,
            setShowInputBar,
            handleSelectOptionRef,
        ],
    );

    const handleComunicaInteracaoResponse = useCallback(
        async (response, newTipoChatArg, respostaBot) => {
            const comunicacaoInternaTypes = [
                'data_inicio_comunicacao_interna',
                'data_fim_comunicacao_interna',
                'cid_comunicacao_interna',
                'comentario_comunicacao_interna',
                'arquivo_comunicacao_interna',
            ];

            if (!comunicacaoInternaTypes.includes(newTipoChatArg)) return false;

            await handleDetectarIntencao(response.data.resposta, newTipoChatArg);
            setMessages((hist) => [...hist, respostaBot]);
            broadcastBotMessage(response.data.resposta);
            return true;
        },
        [handleDetectarIntencao, setMessages, broadcastBotMessage],
    );

    const handleAusenciaResponse = useCallback(
        (newTipoChatArg, respostaBot) => {
            if (newTipoChatArg !== 'informar_ausencia') return false;
            console.log('test 1', newTipoChatArg);
            setMessages((hist) => [...hist, respostaBot]);
            setShowOptions(false);
            setShowInputBar(false);
            broadcastBotMessage(respostaBot.texto);
            return true;
        },
        [setMessages, setShowOptions, setShowInputBar, broadcastBotMessage],
    );

    const handleDefaultBotResponse = useCallback(
        async (tipoChat, newTipoChatArg, respostaBot) => {
            if (tipoChat === 'ausencia_finalizar') {
                handleSelectOptionRef.current?.('ausencia_finalizar', 'Em processo de finalização');
                return;
            }

            setOptions([]);
            setShowOptions(false);

            const label = defaultOptions.find((item) => item.key === newTipoChatArg);
            const optionMessage = `Você está utilizando a opção "${label ? label.label : 'Opção desconhecida'}" `;

            setMessages((hist) => [...hist, respostaBot]);

            const intencaoMenu = await handleDetectarIntencaoMenu(respostaBot.texto);
            const intencaoResp = intencaoMenu.intencao !== 'outro' ? intencaoMenu.intencao : 'chatbot_isa_talk';

            handleSelectOptionRef.current?.(intencaoResp, 'Isa Sugestão');

            broadcastBotMessage(respostaBot.texto);

            setTimeout(() => broadcastBotMessage(optionMessage), 100);
        },
        [
            setMessages,
            setShowOptions,
            broadcastBotMessage,
            handleDetectarIntencaoMenu,
            setOptions,
            handleSelectOptionRef,
        ],
    );

    const handleBotChatResponse = useCallback(
        async (response, tipoChat, newTipoChatArg, tipoChatPergunta) => {
            const humanChatTypes = [
                'AGUARDANDO_HUMANDO',
                'PENDENTE_HUMANO',
                'ATENDIMENTO_AUSENCIA_PENDENTE',
                'ATENDIMENTO_AUSENCIA_EM_ANDAMENTO',
                'COMUNICACAO_RESPONSAVEL_PENDENTE',
                'COMUNICACAO_AT_HUMANDO',
                'conversar_comunicacao_interna',
                'conversar_emprestimo',
                'EMPRESTIMO_EM_ATEND',
            ];

            if (humanChatTypes.includes(tipoChat)) return false;

            const respostaBot = { remetente: 'ISA', texto: response.data.resposta, avatar: LogoImg };

            if (tipoChatPergunta === 'gerar_holerite' || tipoChatPergunta === 'informe_de_rendimento') {
                setShowOptions(true);
                setMessages((hist) => [...hist, respostaBot]);
                setOptions(atendimentoOptions);
                setShowInputBar(true);
                return true;
            }

            const handledComunica = await handleComunicaInteracaoResponse(response, newTipoChatArg, respostaBot);
            if (handledComunica) return true;

            const handled = await handleConsignadoResponse(response, newTipoChatArg, respostaBot);
            if (handled) return true;

            const handledAusencia = handleAusenciaResponse(newTipoChatArg, respostaBot);
            if (handledAusencia) return true;

            handleDefaultBotResponse(tipoChat, newTipoChatArg, respostaBot);
            return true;
        },
        [
            handleComunicaInteracaoResponse,
            handleConsignadoResponse,
            handleAusenciaResponse,
            handleDefaultBotResponse,
            setOptions,
            setMessages,
            setShowOptions,
            setShowInputBar,
        ],
    );

    // ─── Human chat (MQTT) ────────────────────────────────────────────────────

    const handleHumanChatViaMQTT = useCallback(
        (mensagemUsuario, messageText, response) => {
            if (!mqttClientRef.current) return;

            const enhancedMessage = {
                ...mensagemUsuario,
                userId: profile.pess_id_pessoa,
                sessionId: selectedSessionId,
                timestamp: new Date().toISOString(),
                message_id: response.data.message_id,
                userName: profile.pess_nome || 'Usuario',
            };

            mqttClientRef.current.publish(`chat/session/${selectedSessionId}`, JSON.stringify(enhancedMessage));

            broadcastSessionUpdate(selectedSessionId, selectedSession?.status || 'PENDENTE_HUMANO', {
                lastMessage: { mensagem_usuario: messageText, data_mensagem: new Date().toISOString() },
                messageType: 'new_message',
            });

            mqttClientRef.current.publish(
                'chatbot-web/',
                JSON.stringify({
                    sessionId: selectedSessionId,
                    userId: profile.pess_id_pessoa,
                    userName: profile.pess_nome || 'Usuario',
                    messageText,
                    timestamp: new Date().toISOString(),
                    type: 'new_message_received',
                }),
            );
        },
        [mqttClientRef, profile, selectedSessionId, selectedSession, broadcastSessionUpdate],
    );

    // ─── Error response ───────────────────────────────────────────────────────

    const handleErrorResponse = useCallback(
        (error) => {
            console.error('Erro ao enviar mensagem:', error);
            const errorMessage =
                error.response?.data?.detail || error.detail || 'Erro de comunicação. Tente novamente mais tarde.';

            setMessages((prev) => [
                ...prev,
                { remetente: 'ISA', texto: errorMessage, avatar: LogoImgSad, isOption: false },
            ]);
            setOptions(atendimentoOptions);
            setShowOptions(true);
            setShowInputBar(true);

            if (mqttClientRef.current?.connected) {
                mqttClientRef.current.publish(
                    'chatbot-web/',
                    JSON.stringify({
                        sessionId: selectedSessionId,
                        userId: 'ISA',
                        userName: 'ISA',
                        messageText: errorMessage,
                        timestamp: new Date().toISOString(),
                        type: 'new_message_received',
                    }),
                );
            }
        },
        [mqttClientRef, selectedSessionId, setMessages, setOptions, setShowOptions, setShowInputBar],
    );

    // ─── handleBotChatViaMQTT ────────────────────────────────────────────────────

    const handleBotChatViaMQTT = useCallback(
        (mensagemUsuario, messageText, response) => {
            if (!mqttClientRef.current) return;

            const enhancedMessage = {
                ...mensagemUsuario,
                sessionId: selectedSessionId,
                userId: 'ISA',
                userName: 'ISA',
                messageText,
                timestamp: new Date().toISOString(),
                type: 'new_message_received',
            };

            mqttClientRef.current.publish(`chat/session/${selectedSessionId}`, JSON.stringify(enhancedMessage));

            broadcastSessionUpdate(selectedSessionId, selectedSession?.status || 'PENDENTE_HUMANO', {
                lastMessage: { mensagem_usuario: messageText, data_mensagem: new Date().toISOString() },
                messageType: 'new_message',
            });

            mqttClientRef.current.publish(
                'chatbot-web/',
                JSON.stringify({
                    sessionId: selectedSessionId,
                    userId: profile.pess_id_pessoa,
                    userName: profile.pess_nome || 'Usuario',
                    messageText,
                    timestamp: new Date().toISOString(),
                    type: 'new_message_received',
                }),
            );
        },
        [mqttClientRef, profile, selectedSessionId, selectedSession, broadcastSessionUpdate],
    );

    // ─── handleSendMessage ────────────────────────────────────────────────────

    const handleSendMessage = useCallback(async () => {
        if (!newMessage.trim() || !selectedSessionId) return;

        const nomeUsuario = `${profile.pess_nome}-${profile.pess_id_pessoa}`;
        const letraInicial = profile.pess_nome?.[0] ?? 'U';

        let tipoChat = newTipoChat || 'voltar_chat';
        let messageText = newMessage;

        if (
            calcularSeguro &&
            (tipoChat === 'credito_valor_total_calcular_simulacao' || tipoChat === 'credito_calcular_simulacao')
        ) {
            messageText = messageText + calcularSeguro;
        }

        const consignadoResult = processConsignadoOptions(newTipoChat, messageText);
        messageText = consignadoResult.messageText;
        tipoChat = consignadoResult.tipoChat;

        const comunicacaoInternaResult = processComunicacaoInternaOptions(tipoChat, messageText);
        messageText = comunicacaoInternaResult.messageText;
        tipoChat = comunicacaoInternaResult.tipoChat;

        const mensagemUsuario = prepareMessageData(messageText, nomeUsuario, letraInicial);

        setNewMessage('');
        markMessagesAsRead(selectedSessionId);

        if (shouldDisplayMessageImmediately(tipoChat)) {
            setMessages((prev) => [...prev, mensagemUsuario]);
        }

        const reportResult = processReportGeneration(tipoChat, messageText);
        const tipoChatPergunta = reportResult.tipoChatPergunta;
        tipoChat = reportResult.tipoChat;
        messageText = reportResult.messageText;

        if (tipoChatPergunta) {
            setMessages((prev) => [...prev, mensagemUsuario]);
        }

        setIsBotTyping(true);
        const intencaoData = await handleDetectarIntencao(messageText.trim(), tipoChat);

        if (tipoChat === 'voltar_chat') {
            setOptions([]);
            setShowOptions(false);
        }

        try {
            const response = await apiCHAT.post(
                urlChat('chat'),
                { mensagem: messageText.trim(), session_id: selectedSessionId, tipo_chat: tipoChat },
                { headers: { Authorization: `Bearer ${tokenChat}` } },
            );

            if (intencaoData?.intencao === 'envio_de_atestados') {
                await handleSelectOptionRef.current?.(intencaoData.intencao, 'Seleção automatica');
                setIsBotTyping(false);
                return;
            }

            const isBotChat = await handleBotChatResponse(response, tipoChat, newTipoChat, tipoChatPergunta);
            if (!isBotChat) handleHumanChatViaMQTT(mensagemUsuario, messageText, response);

            // Post-response action
            if (tipoChat === 'problema_nao_resolvido_finalizar') {
                handleSelectOptionRef.current?.('problema_nao_resolvido_finalizar', 'Não');
            }
        } catch (error) {
            handleErrorResponse(error);
        } finally {
            setIsBotTyping(false);
        }
    }, [
        apiCHAT,
        tokenChat,
        setShowOptions,
        setOptions,
        newMessage,
        selectedSessionId,
        newTipoChat,
        profile,
        setNewMessage,
        markMessagesAsRead,
        handleSelectOptionRef,
        setMessages,
        setIsBotTyping,
        calcularSeguro,
        urlChat,
        processConsignadoOptions,
        processComunicacaoInternaOptions,
        prepareMessageData,
        shouldDisplayMessageImmediately,
        processReportGeneration,
        handleBotChatResponse,
        handleDetectarIntencao,
        handleHumanChatViaMQTT,
        handleErrorResponse,
    ]);

    // ─── handleFileAttach ─────────────────────────────────────────────────────

    const handleFileAttach = useCallback(
        async (event) => {
            const file = event.target.files[0];
            if (!file || !selectedSessionId) return;

            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
            if (!allowedTypes.includes(file.type)) {
                alert('Arquivo não suportado');
                return;
            }

            const maxSizeInBytes = 10 * 1024 * 1024;
            if (file.size > maxSizeInBytes) {
                alert('O tamanho ultrapassou 10mb');
                return;
            }

            const nomeUsuario = `${profile.pess_nome}-${profile.pess_id_pessoa}`;
            const letraInicial = profile.pess_nome?.[0] ?? 'U';

            const formData = new FormData();
            formData.append('file', file);
            formData.append('session_id', selectedSessionId);

            try {
                const response = await apiCHAT.post('upload-attachment', formData, {
                    headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${tokenChat}` },
                });

                const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
                const fileUrl = response.data.file_url || '';

                const mensagemUsuario = {
                    remetente: nomeUsuario,
                    texto: `Arquivo anexado: ${file.name}`,
                    avatar: letraInicial,
                    is_file: 1,
                    url: fileUrl,
                    ext: fileExtension,
                };

                if (mqttClientRef.current) {
                    mqttClientRef.current.publish(
                        `chat/session/${selectedSessionId}`,
                        JSON.stringify({
                            ...mensagemUsuario,
                            userId: profile.pess_id_pessoa,
                            sessionId: selectedSessionId,
                            timestamp: new Date().toISOString(),
                            userName: profile.pess_nome || 'Usuario',
                        }),
                    );

                    broadcastSessionUpdate(selectedSessionId, selectedSession?.status || 'PENDENTE_HUMANO', {
                        lastMessage: {
                            mensagem_usuario: `Arquivo anexado: ${file.name}`,
                            data_mensagem: new Date().toISOString(),
                        },
                        messageType: 'file_attachment',
                    });

                    mqttClientRef.current.publish(
                        'chatbot-web/',
                        JSON.stringify({
                            sessionId: selectedSessionId,
                            userId: profile.pess_id_pessoa,
                            userName: profile.pess_nome || 'Usuario',
                            messageText: `Arquivo anexado: ${file.name}`,
                            timestamp: new Date().toISOString(),
                            type: 'new_message_received',
                        }),
                    );
                }
            } catch (error) {
                console.error('Erro ao fazer upload do arquivo:', error);
            }
        },
        [apiCHAT, tokenChat, mqttClientRef, profile, selectedSessionId, selectedSession, broadcastSessionUpdate],
    );

    // ─── sendMessageStatus ────────────────────────────────────────────────────

    const sendMessageStatus = (mensagem) => {
        if (mqttClientRef.current) {
            mqttClientRef.current.publish(`chat/session/${selectedSessionId}/status`, JSON.stringify(mensagem));
        }
    };

    // ─── sendTypingStatus ─────────────────────────────────────────────────────

    const sendTypingStatus = useCallback(
        (isTyping) => {
            if (mqttClientRef.current && selectedSessionId) {
                mqttClientRef.current.publish(
                    `chat/session/${selectedSessionId}/typing`,
                    JSON.stringify({
                        userId: profile.pess_id_pessoa,
                        isTyping,
                        timestamp: new Date().toISOString(),
                    }),
                );
            }
        },
        [mqttClientRef, selectedSessionId, profile.pess_id_pessoa],
    );

    // ─── sendOptionAsMessage ──────────────────────────────────────────────────

    const _broadcastBotMsg = (botMessage) => {
        if (mqttClientRef.current?.connected) {
            mqttClientRef.current.publish(
                'chatbot-web/',
                JSON.stringify({
                    sessionId: selectedSessionId,
                    userId: 'ISA',
                    userName: 'ISA',
                    messageText: botMessage,
                    timestamp: new Date().toISOString(),
                    type: 'new_message_received',
                }),
            );
        }
    };

    const _broadcastError = (errorMessage) => {
        if (mqttClientRef.current?.connected) {
            mqttClientRef.current.publish(
                'chatbot-web/',
                JSON.stringify({
                    sessionId: selectedSessionId,
                    userId: 'ISA',
                    userName: 'ISA',
                    messageText: errorMessage,
                    timestamp: new Date().toISOString(),
                    type: 'new_message_received',
                }),
            );
        }
    };

    const sendOptionAsMessage = async (messageText, tipo = false) => {
        if (!messageText.trim() || !selectedSessionId || !tokenChat) return;

        if (!tipo) tipo = newTipoChat;

        const nomeUsuario = `${profile.pess_nome}-${profile.pess_id_pessoa}`;
        const letraInicial = profile.pess_nome?.[0] ?? 'U';

        const mensagemUsuario = {
            remetente: nomeUsuario,
            texto: messageText.trim(),
            avatar: letraInicial,
            is_file: 0,
            url: '',
            ext: '',
        };

        try {
            const response = await apiCHAT.post(
                urlChat('chat'),
                { mensagem: messageText.trim(), session_id: selectedSessionId, tipo_chat: tipo },
                { headers: { Authorization: `Bearer ${tokenChat}` } },
            );

            const respostaBot = { remetente: 'ISA', texto: response.data.resposta, avatar: LogoImg };

            const skipOptionsTypes = [
                'credito_finalizar',
                'informar_ausencia',
                'ausencia_contrato_mensal_cancelar',
                'ausencia_confirmar_escala_trabalho_nao',
                'ausencia_confirmar_viculado_posto_trabalho_sim',
                'ausencia_contrato_mensal_sim',
                'ausencia_confirmar_escala_trabalho_sim',
                'envio_de_atestados',
                'ausencia_finalizar',
                'gerar_holerite',
                'holerite',
                'informe_de_rendimento',
                'isa_pergunta',
                'comunicacao_interna',
                'humano_expressar',
            ];

            const skipOptionsTypesWithRespostaBot = ['isa_pergunta'];

            if (!skipOptionsTypesWithRespostaBot.includes(tipo)) {
                setTimeout(() => handleHumanChatViaMQTT(mensagemUsuario, messageText, response), 100);
            }

            if (!skipOptionsTypes.includes(tipo)) {
                const optionsMessage = 'Escolha uma das opções:';
                setTimeout(() => _broadcastBotMsg(respostaBot.texto), 100);
                setTimeout(() => _broadcastBotMsg(optionsMessage), 100);

                setTimeout(() => handleBotChatViaMQTT(respostaBot, messageText, response), 100);
                setTimeout(() => handleBotChatViaMQTT(optionsMessage, messageText, response), 100);

                _broadcastBotMsg(response.data.resposta);
            } else {
                if (skipOptionsTypesWithRespostaBot.includes(tipo) || tipo === 'humano_expressar') {
                    return true;
                }
                setTimeout(() => _broadcastBotMsg(respostaBot.texto), 120);
                setTimeout(() => handleBotChatViaMQTT(respostaBot, messageText, response), 100);
            }

            setShowOptions(true);
            return true;
        } catch (error) {
            console.error(error);
            const errorMessage =
                error.response?.data?.detail || error.detail || 'Erro de comunicação. Tente novamente mais tarde.';

            setMessages((prev) => [
                ...prev,
                { remetente: 'ISA', texto: errorMessage, avatar: LogoImgSad, isOption: false },
            ]);
            setOptions(atendimentoOptions);
            setShowOptions(true);
            setShowInputBar(false);
            _broadcastError(errorMessage);
            return false;
        }
    };

    // ─── handleComunicacaoInternaFileUpload ───────────────────────────────────

    const handleComunicacaoInternaFileUpload = useCallback(
        () =>
            new Promise((resolve, reject) => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'application/pdf,image/jpeg,image/png,image/gif,image/bmp';

                input.onchange = async (event) => {
                    const file = event.target.files[0];
                    if (!file) {
                        reject('No file selected');
                        return;
                    }
                    if (!selectedSessionId) {
                        reject('Missing session ID');
                        return;
                    }

                    let commData = comunicacaoInternaData;
                    if (!commData?.cmit_id_comunicacao_interna) {
                        try {
                            const fetchCommunicationBySession = async (sessionId) => {
                                const response = await apiCHAT.get(`comunicacao_interna_altum/session/${sessionId}`, {
                                    headers: { Authorization: `Bearer ${tokenChat}` },
                                });
                                return response.data;
                            };
                            commData = await fetchCommunicationBySession(selectedSessionId);
                            if (!commData?.cmit_id_comunicacao_interna) {
                                reject('Could not fetch communication data');
                                return;
                            }
                            setComunicacaoInternaData(commData);
                        } catch (error) {
                            console.error('Error fetching communication data:', error);
                            reject('Failed to fetch communication data');
                            return;
                        }
                    }

                    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
                    if (!allowedTypes.includes(file.type)) {
                        alert('Arquivo não suportado');
                        reject('Unsupported file type');
                        return;
                    }

                    const maxSizeInBytes = 10 * 1024 * 1024;
                    if (file.size > maxSizeInBytes) {
                        alert('O tamanho ultrapassou 10mb');
                        reject('File too large');
                        return;
                    }

                    try {
                        setLoadingPage(true);
                        const nomeUsuario = `${profile.pess_nome}-${profile.pess_id_pessoa}`;
                        const letraInicial = profile.pess_nome?.[0] ?? 'U';

                        await Api.post(`comunicacao-altum/answer/${commData.cmit_id_comunicacao_interna}`, {
                            comentario: 'Anexo',
                            anexo: file,
                        });

                        const formDataAttachment = new FormData();
                        formDataAttachment.append('file', file);
                        formDataAttachment.append('session_id', selectedSessionId);

                        const attachmentResponse = await apiCHAT.post('upload-attachment', formDataAttachment, {
                            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${tokenChat}` },
                        });

                        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
                        const fileUrl = attachmentResponse.data.file_url || '';

                        const mensagemUsuario = {
                            remetente: nomeUsuario,
                            texto: `Arquivo anexado: ${file.name}`,
                            avatar: letraInicial,
                            is_file: 1,
                            url: fileUrl,
                            ext: fileExtension,
                        };

                        if (mqttClientRef.current) {
                            mqttClientRef.current.publish(
                                `chat/session/${selectedSessionId}`,
                                JSON.stringify({
                                    ...mensagemUsuario,
                                    userId: profile.pess_id_pessoa,
                                    sessionId: selectedSessionId,
                                    timestamp: new Date().toISOString(),
                                    userName: profile.pess_nome || 'Usuario',
                                }),
                            );

                            broadcastSessionUpdate(
                                selectedSessionId,
                                selectedSession?.status || 'COMUNICACAO_ANDAMENTO_ARQUIVO',
                                {
                                    lastMessage: {
                                        mensagem_usuario: `Arquivo anexado: ${file.name}`,
                                        data_mensagem: new Date().toISOString(),
                                    },
                                    messageType: 'file_attachment',
                                },
                            );

                            mqttClientRef.current.publish(
                                'chatbot-web/',
                                JSON.stringify({
                                    sessionId: selectedSessionId,
                                    userId: profile.pess_id_pessoa,
                                    userName: profile.pess_nome || 'Usuario',
                                    messageText: `Arquivo anexado: ${file.name}`,
                                    timestamp: new Date().toISOString(),
                                    type: 'new_message_received',
                                }),
                            );
                        }

                        setLoadingPage(false);
                        resolve({ success: true, fileName: file.name });
                    } catch (error) {
                        console.error('Erro ao fazer upload do arquivo:', error);
                        setLoadingPage(false);
                        alert('Erro ao fazer upload do arquivo');
                        reject(error);
                    }
                };

                input.click();
            }),
        [
            apiCHAT,
            tokenChat,
            mqttClientRef,
            profile,
            selectedSessionId,
            selectedSession,
            comunicacaoInternaData,
            setComunicacaoInternaData,
            setLoadingPage,
            broadcastSessionUpdate,
        ],
    );

    return {
        // Exported broadcast helpers (needed by parent for MQTT handler)
        broadcastSessionUpdate,
        broadcastBotMessage,

        // Intention detection (used by HandleSelectOption)
        handleDetectarIntencao,

        // Main handlers
        handleSendMessage,
        handleFileAttach,
        sendMessageStatus,
        sendTypingStatus,
        sendOptionAsMessage,
        handleComunicacaoInternaFileUpload,
    };
};

export default useMessageHandlers;
